import { Link } from "react-router"
import { usePublicEvents, type PublicEvent } from "../api"
import { MicIcon } from "../icons"
import { ArrowUpRight } from "../layout"

const FALLBACK_IMAGES = [
  "/images/home/event-bootcamp.png",
  "/images/home/event-summit.png",
  "/images/home/event-arts.png",
]

export const EVENTS: EventCard[] = [
  {
    id: null,
    image: FALLBACK_IMAGES[0]!,
    alt: "Students at the public speaking bootcamp",
    meta: "Port Harcourt • 01/05/2026 12:00 am",
    title: "Redempta Scholars — Public Speaking Bootcamp",
    description: "Public speaking bootcamp organized by the Redempta Foundation",
  },
  {
    id: null,
    image: FALLBACK_IMAGES[1]!,
    alt: "Attendees at the Tech Innovators Summit",
    meta: "Lagos • 15/05/2026 10:00 am",
    title: "Tech Innovators Summit",
    description: "A summit focusing on startups and technology innovations in Nigeria",
  },
  {
    id: null,
    image: FALLBACK_IMAGES[2]!,
    alt: "Artists at the Creative Arts Festival",
    meta: "Abuja • 30/05/2026 02:00 pm",
    title: "Creative Arts Festival",
    description: "Celebrating local artists and their contributions to culture",
  },
]

export interface EventCard {
  id: string | null
  image: string
  alt: string
  meta: string
  title: string
  description: string
}

function formatDate(event: PublicEvent): string {
  const date = new Date(event.date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
  return `${event.venue ?? event.programName ?? "Redempta"} • ${date} ${event.startTime}`
}

export function toEventCard(event: PublicEvent, index: number): EventCard {
  return {
    id: event.id,
    image: event.bannerUrl ?? FALLBACK_IMAGES[index % FALLBACK_IMAGES.length]!,
    alt: event.title,
    meta: formatDate(event),
    title: event.title,
    description: event.description ?? event.programName ?? "A public event from the Redempta Foundation.",
  }
}

export function EventsSection() {
  const { data } = usePublicEvents()
  const events = data ? data.map(toEventCard) : EVENTS

  return (
    <section className="bg-cream-50 pb-12 lg:pb-24">
      <div className="public-container">
        <span className="inline-flex items-center gap-2.5 rounded-full border border-ink/10 bg-cream-100 px-4 py-1.5 text-sm font-medium text-ink">
          <MicIcon />
          Events
        </span>

        <h2 className="mt-8 font-display text-[36px] font-bold leading-tight text-ink sm:text-[48px] sm:leading-none">
          Events <span className="text-forest-700">&amp;</span> programs
        </h2>

        {events.length === 0 ? (
          <p className="mt-8 text-sm text-ink/60">New public events will appear here soon.</p>
        ) : (
          <div className="mt-8 grid gap-4 md:grid-cols-3 lg:mt-14 lg:gap-6">
            {events.map((event) => (
              <article key={`${event.title}-${event.meta}`} className="flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-sm">
                <img src={event.image} alt={event.alt} className="h-[170px] w-full object-cover lg:h-[300px]" loading="lazy" />
                <div className="flex flex-1 flex-col bg-forest-950 p-4 lg:p-6">
                  <p className="inline-flex items-center gap-2 text-sm text-white/75 lg:text-base">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden>
                      <rect x="3" y="5" width="18" height="17" rx="2" />
                      <path d="M8 2v4M16 2v4M3 10h18" />
                    </svg>
                    {event.meta}
                  </p>
                  <h3 className="mt-3 text-base font-semibold leading-snug text-white">{event.title}</h3>
                  <p className="mt-2 text-sm text-white/80 lg:text-base">{event.description}</p>
                  <div className="mt-auto pt-4 lg:pt-6">
                    <Link to={event.id ? `/events/${event.id}` : "/events"} className="inline-flex items-center gap-2.5 rounded-full border border-white/70 py-1 pl-4 pr-1 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-forest-950">
                      Learn More
                      <span className="grid h-8 w-8 place-items-center rounded-full bg-white text-forest-950">
                        <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
